import backArrow from '../../../../../assets/img/backArrow.png';
import { useState, useEffect, useContext } from 'react';
import { GlobalContext } from 'GlobalContext';
import { USDollar } from 'utils/format';
import api from 'api';
import { isPhoneSize } from 'utils/util';
import { get } from 'utils/fetch';
import { message, Spin, Input, Select, InputNumber } from 'antd';
import { enumOptions } from 'pages/AssetManager/AssetManagerContext';

export function TransactionDetailed() {
    const { manageAssetId, accessToken, assetManager, refreshSession } =
        useContext(GlobalContext);
    const [messageApi, contextHolder] = message.useMessage();
    const { transactionId, setTransactionId } = assetManager;

    // table values
    const [purchasePrice, setPurchasePrice] = useState(0);
    const [underContractDate, setUnderContractDate] = useState('');
    const [purchaseCloseDate, setPurchaseCloseDate] = useState('');
    const [titleBinderInfo, setTitleBinderInfo] = useState('');
    const [company1031Info, setCompany1031Info] = useState('');
    const [targetExitDate, setTargetExitDate] = useState('');
    const [relistingAgent, setRelistingAgent] = useState('');
    const [soldPrice, setSoldPrice] = useState(0);
    const [soldDate, setSoldDate] = useState('');

    // transaction status setup
    const [transactionStatus, setTransactionStatus] = useState(null);
    const [category, setCategory] = useState('');
    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(transactionId === -1 ? false : true);
    const [editMode, setEditMode] = useState(false);
    const [titleHistory, setTitleHistory] = useState(null);

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchTransactionInfo(transactionId) {
        await get(
            api.asset.getTransaction(transactionId),
            setTransaction,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        setLoading(false);
    }

    function restore() {
        setPurchasePrice(transaction?.purchase_price);
        setUnderContractDate(transaction?.under_contract_date);
        setPurchaseCloseDate(transaction?.purchase_close_date);
        setTitleBinderInfo(transaction?.title_binder_info);
        setTitleHistory(transaction?.title_history);
        setCompany1031Info(transaction?.['1031_company_info']);
        setTargetExitDate(transaction?.target_exit_date);
        setRelistingAgent(transaction?.relisting_agent);
        setSoldPrice(transaction?.sold_price);
        setSoldDate(transaction?.sold_date);
        setTransactionStatus(transaction?.transaction_status);
    }

    useEffect(() => {
        if (transaction) {
            restore();
        }
    }, [transaction]);

    useEffect(() => {
        if (transactionId <= 0) {
            setEditMode(false);
            return;
        }
        fetchTransactionInfo(transactionId);
    }, [transactionId]);

    useEffect(() => {
        if (transactionId === -1) {
            setEditMode(true); // auto-enable editing when adding
        } else {
            setEditMode(false); // disable editing until user clicks "Edit"
        }
    }, [transactionId]);

    function unchanged() {
        if (transactionId === -1) {
            return false;
        }
        return (
            purchasePrice === transaction.purchase_price &&
            underContractDate === transaction.under_contract_date &&
            purchaseCloseDate === transaction.purchase_close_date &&
            titleBinderInfo === transaction.title_binder_info &&
            company1031Info === transaction['1031_company_info'] &&
            targetExitDate === transaction.target_exit_date &&
            relistingAgent === transaction.relisting_agent &&
            soldPrice === transaction.sold_price &&
            soldDate === transaction.sold_date &&
            transactionStatus === transaction.transaction_status &&
            titleHistory === transaction.title_history
        );
    }

    async function upsert() {
        if (unchanged()) return;
        setLoading(true);
        const body = JSON.stringify({
            manageAssetId,
            purchasePrice,
            underContractDate,
            transactionStatus,
            purchaseCloseDate,
            titleBinderInfo,
            company1031Info,
            targetExitDate,
            relistingAgent,
            soldDate,
            soldPrice,
            titleHistory,
        });

        const options = {
            method: 'POST', // *GET, POST, PUT, DELETE, etc.
            mode: 'cors', // no-cors, *cors, same-origin
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body,
        };
        const response = await fetch(
            api.asset.upsertTransaction(transactionId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();
        if (idRes === 0) {
            messageApi.error({
                type: 'error', // or 'error', 'info', 'warning', 'loading'
                content:
                    transactionId === -1
                        ? 'Transaction insertion failed'
                        : 'Transaction update failed',
                duration: 3, // seconds (optional)
            });
            setTransactionId(0);
        } else {
            messageApi.open({
                type: 'success', // or 'error', 'info', 'warning', 'loading'
                content:
                    transactionId === -1
                        ? 'Transaction insertion success'
                        : 'Transaction update success',
                duration: 3, // seconds (optional)
            });
            setTransactionId(idRes);
            fetchTransactionInfo(idRes);
        }
        setEditMode(false);
    }

    async function deleteTransaction() {
        setLoading(true);

        const options = {
            method: 'DELETE',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        };

        const response = await fetch(
            api.asset.deleteTransaction(transactionId),
            options
        );
        const data = await response.json();
        const idRes = data.data;
        refreshSession();

        if (idRes === 0) {
            messageApi.error({
                type: 'error',
                content: 'Transaction deletion failed',
                duration: 3,
            });
        } else {
            messageApi.open({
                type: 'success',
                content: 'Transaction deletion success',
                duration: 3,
            });
            setTransactionId(0);
        }
    }

    const entryStyle = {
        margin: '20px',
        fontSize: '20px',
    };

    const inputStyle = {
        border: '0',
        outline: 'none',
        borderBottom: '1px solid black',
        fontSize: '20px',
        background: 'transparent',
    };

    return (
        <>
            {contextHolder}
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            marginLeft: '20px',
                            position: 'fixed',
                            background: 'white',
                            width: '100%',
                            zIndex: 1,
                            height: '40px',
                            alignItems: 'end',
                        }}
                    >
                        <img
                            style={{
                                width: '15px',
                                height: '20px',
                                cursor: 'pointer',
                            }}
                            src={backArrow}
                            onClick={() => {
                                setTransactionId(0);
                            }}
                        ></img>
                        {transactionId !== -1 && (
                            <div
                                style={{
                                    width: 'fit-content',
                                    color: 'blue',
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    marginLeft: '20px',
                                }}
                                onClick={() => {
                                    if (editMode) {
                                        restore();
                                    }
                                    setEditMode((prev) => !prev);
                                }}
                            >
                                {editMode ? 'Restore' : 'Edit'}
                            </div>
                        )}
                    </div>

                    <div style={{ paddingTop: '20px' }}>
                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : {
                                          ...entryStyle,
                                          display: 'flex',
                                          marginTop: '40px',
                                      }
                            }
                        >
                            <div>Purchase Price: </div>
                            {editMode ? (
                                <InputNumber
                                    value={purchasePrice}
                                    onChange={(value) =>
                                        setPurchasePrice(value)
                                    }
                                    placeholder="Enter purchase price"
                                    style={{
                                        border: 0,
                                        borderBottom: '1px solid black',
                                        outline: 'none',
                                        width: '150px',
                                    }}
                                    controls={false} // removes the up/down arrows
                                />
                            ) : (
                                <div>{USDollar.format(purchasePrice)}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Under Contract Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={underContractDate || ''}
                                    onChange={(e) =>
                                        setUnderContractDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{underContractDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Transaction Status: </div>
                            {editMode ? (
                                <>
                                    <Select
                                        style={{ width: 250 }}
                                        placeholder="select transaction status"
                                        value={transactionStatus}
                                        onChange={setTransactionStatus}
                                        options={enumOptions[
                                            'Transaction Status'
                                        ].map((s) => ({
                                            value: s,
                                            label: s,
                                        }))}
                                        allowClear
                                    />
                                </>
                            ) : (
                                <div>{transactionStatus}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Purchase Close Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={purchaseCloseDate || ''}
                                    onChange={(e) =>
                                        setPurchaseCloseDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{purchaseCloseDate}</div>
                            )}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '400px',
                                marginBottom: '4px',
                            }}
                        >
                            <div
                                style={{ fontSize: '20px', marginLeft: '20px' }}
                            >
                                Title Binder Info:
                            </div>
                            {editMode ? (
                                <textarea
                                    style={{
                                        outline: 'none',
                                        border: '1px solid black',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        resize: 'vertical', // allows user to resize vertically
                                        minHeight: '100px',
                                        fontFamily: 'inherit',
                                        fontSize: '14px',
                                        marginLeft: '20px',
                                    }}
                                    value={titleBinderInfo || ''}
                                    onInput={(e) => {
                                        const value = e.target.value;
                                        setTitleBinderInfo(value);
                                    }}
                                ></textarea>
                            ) : (
                                <div
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        backgroundColor: '#f9f9f9',
                                        whiteSpace: 'pre-wrap',
                                        minHeight: '34px',
                                        marginLeft: '20px',
                                    }}
                                >
                                    {titleBinderInfo}
                                </div>
                            )}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '400px',
                                marginBottom: '4px',
                            }}
                        >
                            <div
                                style={{ fontSize: '20px', marginLeft: '20px' }}
                            >
                                Title History:
                            </div>
                            {editMode ? (
                                <textarea
                                    style={{
                                        outline: 'none',
                                        border: '1px solid black',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        resize: 'vertical', // allows user to resize vertically
                                        minHeight: '100px',
                                        fontFamily: 'inherit',
                                        fontSize: '14px',
                                        marginLeft: '20px',
                                    }}
                                    value={titleHistory || ''}
                                    onInput={(e) => {
                                        const value = e.target.value;
                                        setTitleHistory(value);
                                    }}
                                ></textarea>
                            ) : (
                                <div
                                    style={{
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        padding: '8px',
                                        backgroundColor: '#f9f9f9',
                                        whiteSpace: 'pre-wrap',
                                        minHeight: '34px',
                                        marginLeft: '20px',
                                    }}
                                >
                                    {titleHistory}
                                </div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>1031 Company Info: </div>
                            {editMode ? (
                                <input
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    type="text"
                                    value={company1031Info || ''}
                                    onInput={(e) => {
                                        const value = e.target.value;
                                        setCompany1031Info(value);
                                    }}
                                ></input>
                            ) : (
                                <div>{company1031Info}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Target Exit Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={targetExitDate || ''}
                                    onChange={(e) =>
                                        setTargetExitDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{targetExitDate}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Relisting Agent:</div>
                            {editMode ? (
                                <input
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    type="text"
                                    value={relistingAgent || ''}
                                    onInput={(e) => {
                                        const value = e.target.value;
                                        setRelistingAgent(value);
                                    }}
                                ></input>
                            ) : (
                                <div>{relistingAgent}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Sold Price: </div>
                            {editMode ? (
                                <InputNumber
                                    value={soldPrice}
                                    onChange={(value) => setSoldPrice(value)}
                                    placeholder="Sold Price"
                                    style={{
                                        border: 0,
                                        borderBottom: '1px solid black',
                                        outline: 'none',
                                        width: '150px',
                                    }}
                                    controls={false} // hides the up/down arrows
                                    precision={2} // optional, for two decimal places
                                />
                            ) : (
                                <div>{USDollar.format(soldPrice)}</div>
                            )}
                        </div>

                        <div
                            style={
                                isPhoneSize()
                                    ? entryStyle
                                    : { ...entryStyle, display: 'flex' }
                            }
                        >
                            <div>Sold Date: </div>
                            {editMode ? (
                                <input
                                    type="date"
                                    style={{
                                        outline: 'none',
                                        border: 0,
                                        borderBottom: '1px solid black',
                                    }}
                                    value={soldDate || ''}
                                    onChange={(e) =>
                                        setSoldDate(e.target.value)
                                    }
                                ></input>
                            ) : (
                                <div>{soldDate}</div>
                            )}
                        </div>
                    </div>

                    <button
                        style={{
                            height: '30px',
                            width: '60px',
                            borderRadius: '30px',
                            background: 'rgb(17 32 86)',
                            color: 'white',
                            cursor: 'pointer',
                            marginLeft: '20px',
                        }}
                        onClick={upsert}
                    >
                        Save
                    </button>

                    <button
                        style={{
                            height: '30px',
                            width: '60px',
                            borderRadius: '30px',
                            background: 'rgb(86 17 17)',
                            color: 'white',
                            cursor: 'pointer',
                        }}
                        onClick={() => {
                            if (
                                window.confirm(
                                    'Are you sure you want to delete this transaction?'
                                )
                            ) {
                                deleteTransaction();
                            }
                        }}
                    >
                        Delete
                    </button>
                </>
            )}
        </>
    );
}
